import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Toaster, toast } from 'sonner'
import { useAuthStore } from './stores/auth'
import { useCatalogStore } from './stores/catalog'
import { UNAUTHORIZED_EVENT, registrarProveedorSesion } from './lib/api'
import { versionGenerada } from './lib/entity-versions.generated'
import { inicializarAsyncapiDevtools } from './lib/asyncapi-devtools'
import { LoginPage } from './pages/LoginPage'
import { SelectCoPage } from './pages/SelectCoPage'
import { HomePage } from './pages/HomePage'
import { ChatHomePage } from './pages/ChatHomePage'
import { esHostCliente } from './lib/config'
import { DashboardLayout } from './components/layout/DashboardLayout'
import { ThemeProvider } from './components/ThemeProvider'
import { ErrorBoundary } from './components/ErrorBoundary'
import { BrandingProvider } from './components/BrandingProvider'
import { Skeleton } from './components/ui/skeleton'

// CUI-2 (orden 0eab5681): inyección del estado de sesión/catálogo en api.ts sin
// ciclo de imports — habilita el auth-ready gating y el routing version-agnóstico
// centralizados (propiedades heredables del conector; ver lib/api.ts).
registrarProveedorSesion(() => {
  const a = useAuthStore.getState()
  const c = useCatalogStore.getState()
  return {
    token: a.token,
    coId: a.activeCoId,
    catalogoListo: c.loaded,
    cargarCatalogo: () => c.load(a.token),
    recargarCatalogo: () => c.reload(a.token),
    // Catálogo vivo (primario, autobumps §13 en scope) → mapa generado en build
    // (autoritativo para entidades chrome/branding fuera del subset RBAC del cliente;
    // mismo criterio que vsn()). Mata el root CRITERIO-VERSION en reescribirVersion.
    versionDe: (sub, token, estado) =>
      c.entidades.find((e) => e.sub === sub && e.token === token && e.estado === estado)?.version ||
      versionGenerada(sub, token, estado) ||
      null,
  }
})

// G.3.18: code-splitting lazy-load — cada ruta page va a su propio chunk.
// Initial bundle reducido de ~520KB a ~200KB (core: auth + home + layout).
// Resto carga on-demand al navegar.
const CatalogoPage = lazy(() => import('./pages/CatalogoPage').then((m) => ({ default: m.CatalogoPage })))
const EntidadDetallePage = lazy(() => import('./pages/EntidadDetallePage').then((m) => ({ default: m.EntidadDetallePage })))
const EntidadOperativaPage = lazy(() => import('./pages/EntidadOperativaPage').then((m) => ({ default: m.EntidadOperativaPage })))
const DmnEntidadPage = lazy(() => import('./pages/DmnEntidadPage').then((m) => ({ default: m.DmnEntidadPage })))
const CrearInstanciaPage = lazy(() => import('./pages/CrearInstanciaPage').then((m) => ({ default: m.CrearInstanciaPage })))
const CrearSuscripcionPage = lazy(() => import('./pages/CrearSuscripcionPage').then((m) => ({ default: m.CrearSuscripcionPage })))
const VidaTokenPage = lazy(() => import('./pages/VidaTokenPage').then((m) => ({ default: m.VidaTokenPage })))
const MonitorizacionPage = lazy(() => import('./pages/MonitorizacionPage').then((m) => ({ default: m.MonitorizacionPage })))
const InstanciaDetallePage = lazy(() => import('./pages/InstanciaDetallePage').then((m) => ({ default: m.InstanciaDetallePage })))
const SuscripcionesPage = lazy(() => import('./pages/SuscripcionesPage').then((m) => ({ default: m.SuscripcionesPage })))
const MarketplacePage = lazy(() => import('./pages/MarketplacePage').then((m) => ({ default: m.MarketplacePage })))
const GeneradorEntidadPage = lazy(() => import('./pages/GeneradorEntidadPage').then((m) => ({ default: m.GeneradorEntidadPage })))
const BrandingPage = lazy(() => import('./pages/BrandingPage').then((m) => ({ default: m.BrandingPage })))
const WizardClienteNuevoPage = lazy(() => import('./pages/WizardClienteNuevoPage').then((m) => ({ default: m.WizardClienteNuevoPage })))
const VisorAppPage = lazy(() => import('./pages/VisorAppPage').then((m) => ({ default: m.VisorAppPage })))
const OnboardingCoPage = lazy(() => import('./pages/OnboardingCoPage').then((m) => ({ default: m.OnboardingCoPage })))
const MemoriaAgentePage = lazy(() => import('./pages/MemoriaAgentePage').then((m) => ({ default: m.MemoriaAgentePage })))
const GobiernoPage = lazy(() => import('./pages/GobiernoPage').then((m) => ({ default: m.GobiernoPage })))
const AcercaDePage = lazy(() => import('./pages/AcercaDePage').then((m) => ({ default: m.AcercaDePage })))
const ModulosPage = lazy(() => import('./pages/ModulosPage').then((m) => ({ default: m.ModulosPage })))

function LazyFallback() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
    </div>
  )
}

function IndexRedirect() {
  const isAuthed = useAuthStore((s) => !!s.token)
  const requiresCoSelection = useAuthStore((s) => s.requiresCoSelection)
  const activeCoId = useAuthStore((s) => s.activeCoId)
  if (!isAuthed) return <Navigate to="/login" replace />
  if (requiresCoSelection || !activeCoId) return <Navigate to="/select-co" replace />
  return <Navigate to="/app" replace />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthed = useAuthStore((s) => !!s.token)
  const requiresCoSelection = useAuthStore((s) => s.requiresCoSelection)
  const activeCoId = useAuthStore((s) => s.activeCoId)
  if (!isAuthed) return <Navigate to="/login" replace />
  if (requiresCoSelection || !activeCoId) return <Navigate to="/select-co" replace />
  return <>{children}</>
}

function LoginRoute() {
  const isAuthed = useAuthStore((s) => !!s.token)
  if (isAuthed) return <Navigate to="/" replace />
  return <LoginPage />
}

function SelectCoRoute() {
  const isAuthed = useAuthStore((s) => !!s.token)
  if (!isAuthed) return <Navigate to="/login" replace />
  return <SelectCoPage />
}

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LazyFallback />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

export default function App() {
  // 401 en cualquier llamada autenticada → cerrar sesión. ProtectedRoute redirige a /login
  // al desaparecer el token. Listener global (api.ts emite el evento sin acoplarse al store).
  useEffect(() => {
    function onUnauthorized() {
      if (!useAuthStore.getState().token) return // ya cerrada, evita toasts duplicados
      useAuthStore.getState().logout()
      toast.info('Tu sesión expiró. Vuelve a iniciar sesión.')
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [])

  // Conexión del organismo (AsyncAPI) volcada en DevTools al autenticarse — máxima info
  // del bus reactivo para inspección. Una vez por sesión (el módulo es idempotente).
  // Gateado a token + CO RESUELTO: `?scope=mio` necesita el co_canonico del JWT; dispararlo
  // antes de tener CO devolvía 403 (y el guard reintentaba → 3× 403 en consola).
  useEffect(() => {
    const tryInit = (s: { token: string | null; activeCoId: string | null }) => {
      if (s.token && s.activeCoId) inicializarAsyncapiDevtools(s.token)
    }
    tryInit(useAuthStore.getState())
    const unsub = useAuthStore.subscribe((s) => tryInit(s))
    return () => unsub()
  }, [])

  return (
    <ThemeProvider defaultTheme="system">
      <BrandingProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<IndexRedirect />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/select-co" element={<SelectCoRoute />} />

            <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              {/* PWA-3: en hosts de cliente la home es el chat-agente (superficie primaria);
                  en el Arquitecto, el dashboard. Derivado del host, cero hardcode de cliente. */}
              <Route index element={esHostCliente() ? <ChatHomePage /> : <HomePage />} />
              <Route path="catalogo" element={<LazyPage><CatalogoPage /></LazyPage>} />
              <Route path="catalogo/:subdominio/:token/:estado/:version" element={<LazyPage><EntidadDetallePage /></LazyPage>} />
              <Route path="operar/:subdominio/:token/:estado" element={<LazyPage><EntidadOperativaPage /></LazyPage>} />
              <Route path="dmn/:subdominio/:token/:estado/:version" element={<LazyPage><DmnEntidadPage /></LazyPage>} />
              <Route path="crear/:subdominio/:token/:estado/:version" element={<LazyPage><CrearInstanciaPage /></LazyPage>} />
              <Route path="contratar" element={<LazyPage><CrearSuscripcionPage /></LazyPage>} />
              <Route path="vida/:genesisId" element={<LazyPage><VidaTokenPage /></LazyPage>} />
              {/* F2 (orden a79be419): un solo modelo de observación — el per-CO suelto se
                  unifica al agregado §32 de Monitorización. La vista per-CO sigue viva como
                  `dashboard` dentro de Monitorización→Fichas. */}
              <Route path="instancias/mias" element={<Navigate to="/app/monitorizacion/fichas" replace />} />
              <Route path="instancias/:subdominio/:token/:estado/:version/:instanciaId" element={<LazyPage><InstanciaDetallePage /></LazyPage>} />
              <Route path="suscripciones" element={<LazyPage><SuscripcionesPage /></LazyPage>} />
              <Route path="marketplace" element={<LazyPage><MarketplacePage /></LazyPage>} />
              <Route path="generar-entidad" element={<LazyPage><GeneradorEntidadPage /></LazyPage>} />
              <Route path="branding" element={<LazyPage><BrandingPage /></LazyPage>} />
              <Route path="nuevo-cliente" element={<LazyPage><WizardClienteNuevoPage /></LazyPage>} />
              <Route path="visor" element={<LazyPage><VisorAppPage /></LazyPage>} />
              <Route path="onboarding-co" element={<LazyPage><OnboardingCoPage /></LazyPage>} />
              <Route path="visor/:denominacion" element={<LazyPage><VisorAppPage /></LazyPage>} />
              <Route path="observabilidad" element={<Navigate to="/app/monitorizacion/resumen" replace />} />
              <Route path="monitorizacion" element={<Navigate to="/app/monitorizacion/resumen" replace />} />
              <Route path="monitorizacion/:seccion" element={<LazyPage><MonitorizacionPage /></LazyPage>} />
              <Route path="memoria-agente" element={<LazyPage><MemoriaAgentePage /></LazyPage>} />
              <Route path="gobierno" element={<LazyPage><GobiernoPage /></LazyPage>} />
              <Route path="acerca-de" element={<LazyPage><AcercaDePage /></LazyPage>} />
              {/* AC-3: módulos por intersección de 3 anillos (superficies ∩ §32 ∩ RBAC). */}
              <Route path="modulos" element={<LazyPage><ModulosPage /></LazyPage>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors closeButton position="top-right" />
      </ErrorBoundary>
      </BrandingProvider>
    </ThemeProvider>
  )
}
